import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { EditComponent } from '@workspaces/utils/edit-component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { WorkspaceDocVersion } from '@workspaces/models/versions';
import { workspaceVersionFeature, WorkspaceVersionFetch } from '@workspaces/store/workspace-version.feature';
import { combineLatest, Observable, startWith, Subscription } from 'rxjs';
import { VersionDifferComponent } from '../version-differ/version-differ.component';
import { WorkspaceVersionService } from '@workspaces/services/version.service';
import { MatButtonModule } from '@angular/material/button';
import { VersionListItemComponent } from './version-list-item/version-list-item.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkAccordion, CdkAccordionItem } from '@angular/cdk/accordion';
import { animate, state, style, transition, trigger } from '@angular/animations';

interface HourlyGroup {
  hour: string;
  latestVersion: WorkspaceDocVersion;
  otherVersions: WorkspaceDocVersion[];
  isExpanded: boolean;
}

interface DayGroup {
  date: string;
  hourlyGroups: HourlyGroup[];
}

@Component({
  selector: 'app-version-history',
  standalone: true,
  imports: [
    CommonModule,
    MatListModule,
    MatDividerModule,
    MatIconModule,
    MatChipsModule,
    VersionDifferComponent,
    MatButtonModule,
    VersionListItemComponent,
    MatFormField,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    MatOption,
    MatProgressBar,
    MatTooltipModule,
    CdkAccordion,
    CdkAccordionItem
  ],
  templateUrl: './version-history.component.html',
  styleUrls: ['./version-history.component.scss', '../../global-workspace-styles.scss'],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('bodyExpansion', [
      state('collapsed, void', style({ height: '0px', visibility: 'hidden' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed, void => collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class VersionHistoryComponent extends EditComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();
  public groupedVersions: DayGroup[] = [];

  public showAdded = true;
  public showDeleted = true;
  private timeSortedVersions: WorkspaceDocVersion[] = [];
  public currentSelectedVersionId = '';
  public currentCompareHtml = '';
  public versionTypeSelector = new FormControl<'all' | 'onlyNamed'>('all');
  public isVersionsLoading$!: Observable<boolean>;
  public htmlIsLoading = false;
  public isFirstVersionId = '';
  constructor(
    protected override readonly route: ActivatedRoute,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly datePipe: DatePipe,
    private readonly versionService: WorkspaceVersionService
  ) {
    super(route, snackbar, router);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.store.dispatch(WorkspaceVersionFetch({ workspaceId: this.workspaceId }));
    this.isVersionsLoading$ = this.store.select(workspaceVersionFeature.workspaceVersionIsLoading(this.workspaceId));
    this.sub.add(
      combineLatest([
        this.versionTypeSelector.valueChanges.pipe(startWith('all')),
        this.store.select(workspaceVersionFeature.workspaceVersionData(this.workspaceId))
      ]).subscribe(([type, versions]) => {
        this.timeSortedVersions = this.sortVersions(versions);
        this.isFirstVersionId = this.timeSortedVersions[this.timeSortedVersions.length - 1]?.version_id ?? '';
        const usedVersions =
          type === 'all' ? this.timeSortedVersions : this.timeSortedVersions.filter(version => !!version.alias);
        this.groupedVersions = this.groupVersions(usedVersions);
        this.changeDetectorRef.markForCheck();
      })
    );
  }

  onVersionClick(versionId: string) {
    this.currentSelectedVersionId = versionId;
    const selectedVersionIndex = this.timeSortedVersions.findIndex(version => version.version_id === versionId);
    const previousVersion = this.timeSortedVersions[selectedVersionIndex + 1];
    if (!previousVersion || selectedVersionIndex === -1) {
      return;
    }
    //We dont cache html as it can be too big
    this.htmlIsLoading = true;
    this.sub.add(
      this.versionService
        .compareVersions(this.workspaceId, previousVersion.version_id, versionId)
        .subscribe(comapredHtml => {
          this.currentCompareHtml = comapredHtml.diff_html;
          this.changeDetectorRef.markForCheck();
        })
        .add(() => {
          this.htmlIsLoading = false;
        })
    );
  }

  getDayVersionCount(dayGroup: DayGroup): number {
    return dayGroup.hourlyGroups.reduce((acc, h) => acc + h.otherVersions.length + 1, 0);
  }

  private sortVersions(versions: WorkspaceDocVersion[]) {
    if (!versions || versions.length === 0) {
      return [];
    }

    const localClonedObject = structuredClone(versions); //Clone as ngrx is immutable
    const sortedVersions = localClonedObject.sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    return sortedVersions;
  }
  private groupVersions(versions: WorkspaceDocVersion[]): DayGroup[] {
    const versionsByDayandHour = this.createUniqueGroupping(versions);

    return this.createDayGroups(versionsByDayandHour);
  }

  private createUniqueGroupping(sortedVersions: WorkspaceDocVersion[]) {
    const versionsByDay: Record<string, Record<string, WorkspaceDocVersion[]>> = {};

    sortedVersions.forEach(version => {
      const dayKey = this.datePipe.transform(version.created_at, 'longDate') || 'Unknown Date';
      const hourKey = this.datePipe.transform(version.created_at, 'h a') || 'Unknown Hour';

      if (!versionsByDay[dayKey]) {
        versionsByDay[dayKey] = {};
      }

      if (!versionsByDay[dayKey][hourKey]) {
        versionsByDay[dayKey][hourKey] = [];
      }

      versionsByDay[dayKey][hourKey].push(version);
    });
    return versionsByDay;
  }

  private createDayGroups(versionsByDay: Record<string, Record<string, WorkspaceDocVersion[]>>): DayGroup[] {
    const groupedDayVersions: DayGroup[] = [];

    for (const dayKey of Object.keys(versionsByDay)) {
      const hourlyMap = versionsByDay[dayKey];

      const hourlyGroups = this.createHourGroups(hourlyMap);

      groupedDayVersions.push({
        date: dayKey,
        hourlyGroups: hourlyGroups
      });
    }

    return groupedDayVersions;
  }

  private createHourGroups(hourlyMap: Record<string, WorkspaceDocVersion[]>): HourlyGroup[] {
    const hourlyGroups: HourlyGroup[] = [];

    const sortedHourKeys = Object.keys(hourlyMap);

    for (const hourKey of sortedHourKeys) {
      const versionsInHour = hourlyMap[hourKey];

      const latestVersion = versionsInHour[0];
      const otherVersions = versionsInHour.slice(1);

      hourlyGroups.push({
        hour: hourKey,
        latestVersion: latestVersion,
        otherVersions: otherVersions,
        isExpanded: false
      });
    }

    return hourlyGroups;
  }

  toggleHourExpansion(group: HourlyGroup): void {
    group.isExpanded = !group.isExpanded;
    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
